/** Pure helpers for ticket code panel (unit-tested; used by EnhancedTicketDetail). */

export function extractCodeFromBackticks(content) {
  if (!content) return content;

  const codeBlockRegex = /```[\s\S]*?```/g;
  const matches = content.match(codeBlockRegex);

  if (matches && matches.length > 0) {
    return matches
      .map((match) =>
        match.replace(/^```[\w]*\n?/, '').replace(/\n?```$/, '')
      )
      .join('\n\n');
  }

  return content;
}

export function hasDeployableCode(code) {
  const trimmed = (code || '').trim();
  return trimmed.length > 0 && !trimmed.startsWith('// Code will appear here');
}

export function normalizeTicketType(type) {
  return (type || '').trim().toLowerCase().replace(/_/g, ' ');
}

export function isCodeReportTicket(ticketOrType) {
  const raw =
    typeof ticketOrType === 'string'
      ? ticketOrType
      : ticketOrType?.customization_type || ticketOrType?.category || '';
  return normalizeTicketType(raw) === 'code report';
}

/** Pick active code row the UI should display (newest active, else newest). */
export function pickActiveCodeGeneration(rows) {
  if (!rows || rows.length === 0) return null;
  const active = rows.filter((r) => r.is_active);
  if (active.length === 1) return active[0];
  if (active.length > 1) {
    return [...active].sort(
      (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
    )[0];
  }
  return rows[0];
}

/** Security tickets: show full agent message (never last-line truncation). */
export function processSecurityMessage(content) {
  return content || '';
}

const SECURITY_COT_RESPONSE_MARKER = '--- SAP Auth Agent Response ---';

/** Fallback when GET /code returns empty but master-agent appended to CoT. */
export function extractSecurityMessageFromCoT(chainOfThought) {
  const cot = chainOfThought || '';
  const markerIdx = cot.indexOf(SECURITY_COT_RESPONSE_MARKER);
  if (markerIdx >= 0) {
    return cot.slice(markerIdx + SECURITY_COT_RESPONSE_MARKER.length).trim();
  }
  return '';
}

/** Prefer code API payload; fall back to CoT append block on deploy. */
export function resolveSecurityAgentMessage(chainOfThought, codeApiMessage) {
  const fromCode = processSecurityMessage(codeApiMessage);
  if (fromCode && fromCode.trim().length > 0) {
    return fromCode;
  }
  return extractSecurityMessageFromCoT(chainOfThought);
}

export function isSecurityTicketType(ticketOrType) {
  return normalizeTicketType(
    typeof ticketOrType === 'string'
      ? ticketOrType
      : ticketOrType?.customization_type || ticketOrType?.category || ''
  ) === 'security';
}

/** True while the Security agent is actively working (CoT markers). */
export function isSecurityAgentRunning(chainOfThought) {
  const cot = (chainOfThought || '').toLowerCase();
  if (!cot) return false;
  if (isSecurityAgentComplete(chainOfThought)) return false;
  if (isSecurityAgentFailed(chainOfThought)) return false;
  return (
    cot.includes('running security agent') ||
    cot.includes('🔐 running security') ||
    cot.includes('chain of thinking session started')
  );
}

/** True when the Security ticket run finished successfully. */
export function isSecurityAgentComplete(chainOfThought, agentMessage = '') {
  const msg = (agentMessage || '').trim();
  if (msg.length > 0) return true;
  const cot = (chainOfThought || '').toLowerCase();
  return (
    cot.includes('security request complete') ||
    cot.includes('✅ security request complete')
  );
}

/** True when the Security ticket run failed. */
export function isSecurityAgentFailed(chainOfThought, agentMessage = '') {
  const cot = (chainOfThought || '').toLowerCase();
  if (
    cot.includes('security request failed') ||
    cot.includes('❌ security request failed')
  ) {
    return true;
  }
  const msg = (agentMessage || '').toLowerCase();
  return (
    msg.includes('error when retrieving') ||
    msg.includes('not a valid tool') ||
    msg.includes('security agent error')
  );
}
